/* eslint-disable no-param-reassign */
// Framework dependencies
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { NextPage } from 'next';
import { useTracking } from 'react-tracking';
import { useRouter } from 'next/router';
import { QueryResult, useQuery } from '@apollo/client';
import {
  GetUsersUtilityProvidersQuery,
  GetUtilitiesQuery,
  GetDivision,
} from 'utils/queries';
import { useRESTClient } from 'context';
import { useApolloClient } from '@apollo/client';

// Packages dependencies
import { TrackEventInput } from '@updater/ui-tracker';

// Local dependencies
import { GridCardCtaStatus } from 'components/logo-grid-card';
import { PageTemplate } from 'components/page-template';
import {
  availableUtilityProviders,
  availableUtilityProvidersVariables,
  availableUtilityProviders_availableUtilityProviders_utilityProviders as AvailableUtilityProviders,
} from 'types/generated/availableUtilityProviders';
import { getDivision, getDivisionVariables } from 'types/generated/getDivision';
import {
  getUtilities,
  getUtilitiesVariables,
} from 'types/generated/getUtilities';
import { UtilityKinds } from '../../../../__generated__/globalTypes';

import styled from '@emotion/styled';
import {
  GridCardLayout,
  Palette,
  Text,
  Image,
  ColorKey,
  Button,
  Box,
} from '@updater/ui-uds';
import { Drop, Fire, Lightning, CheckCircle } from '@phosphor-icons/react';
import {
  GridCardColumn,
  GridCardColumnBottom,
} from 'components/logo-grid-card/LogoGridCard.styles';
import { UtilityCard } from 'components/utility-card/UtilityCard';
import { useGetUserInfoQuery } from 'flows/core/hooks/use-get-user-info-query';
import { useSWRWhenAuthed } from 'utils/use-swr-when-authed';
import { DOMAIN, PAGE, OBJECT, VERB } from '../constants/analytics-tracker';
import { COPY, CARD_CONTENT, UTILITIES } from '../constants/copy';
import { ERROR_PAGE_PATH } from '../constants';

import { pickUtilities } from '../utils';
import { ResponsiveValue, Theme } from 'styled-system';
import { getUserInfo, getUserInfoVariables } from 'types/generated/getUserInfo';
import { useShouldRouteToUPODaisyChain } from 'flows/core/hooks/use-daisy-chain';

const StyledCardsLayout = styled(GridCardLayout)`
  padding-top: ${(props) => props.theme.space.l}px;
  margin-left: 0;
  margin-right: 0;
`;

export const enabledOfKind =
  (kindToCompare: UtilityKinds) =>
  (element: { enabled?: boolean; kind?: UtilityKinds }) => {
    return element.enabled && element.kind === kindToCompare;
  };

const SuccessBox = styled(GridCardColumnBottom)`
  flex-direction: row;
  align-items: center;
`;

const spanGridSetting = ['4', null, '5', null];

export const UtilityProviderCard = ({
  card,
}: {
  card: AvailableUtilityProviders;
}) => {
  const [logoErrored, setLogoErrored] = useState(false);
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: DOMAIN.UTILITIES,
  });
  const { id, name, kind, businessPhone, localPhone, website, logoImage } =
    card;

  const logoSrc = logoImage?.id;

  // This is an error state if the logo is a broken image;
  // we're leveraging the native img onError function as a callback here.
  // Let the browser do the heavy lifting:
  const getFallbackLogo = (ukind: UtilityKinds) => {
    switch (ukind) {
      case UtilityKinds.gas:
        return (
          <Fire
            size={40}
            weight="thin"
            color={Palette.gray50}
            data-testid="gas"
          />
        );
      case UtilityKinds.electric:
        return (
          <Lightning
            size={40}
            weight="thin"
            color={Palette.gray50}
            data-testid="electric"
          />
        );
      case UtilityKinds.water:
        return (
          <Drop
            size={40}
            weight="thin"
            color={Palette.gray50}
            data-testid="water"
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <UtilityCard
      data-testid="utility-card"
      key={`card-${id}`}
      span={spanGridSetting}
      spanStart="1"
      logo={
        logoErrored ? (
          getFallbackLogo(kind as UtilityKinds)
        ) : (
          // eslint-ignore-next-line @next/next/no-img-element
          <Image
            src={logoSrc}
            onError={() => setLogoErrored(true)}
            alt={`${name} logo`}
          />
        )
      }
      title={UTILITIES[kind as keyof typeof UTILITIES]}
      subtitle={
        (!name && !logoSrc
          ? 'We’re having trouble locating your provider. Check back soon!'
          : name) as string
      }
      phoneNumber={
        (!name && !logoSrc ? undefined : businessPhone || localPhone) as
          | string
          | undefined
      }
      ctaCopy={CARD_CONTENT.visitWebsite}
      href={(!name && !logoSrc ? undefined : website) as string | undefined}
      onClick={() => {
        trackEvent({
          pageName: PAGE.INDEX,
          object: OBJECT.CTA_BUTTON,
          verb: VERB.CLICKED,
          details: {
            provider: name,
            providerId: id,
          },
        });
      }}
      // Based on the issues in SC-90172 I believe this code was incorrect, removing for now
      // status={enabled ? GridCardCtaStatus.LINKOUT : GridCardCtaStatus.SUCCESS}
      status={GridCardCtaStatus.LINKOUT}
    />
  );
};

export const UtilityProviderGroup = ({
  providerGroup,
}: {
  providerGroup?: {
    type: string;
    providers: AvailableUtilityProviders[];
  };
}) => {
  return (
    <>
      {providerGroup && (
        <Text variant="m" mt="xl">
          {providerGroup.type}
        </Text>
      )}
      <StyledCardsLayout>
        {providerGroup?.providers?.length ? (
          providerGroup.providers.map((provider) => {
            return <UtilityProviderCard card={provider} key={provider.id} />;
          })
        ) : (
          <GridCardColumn
            span={spanGridSetting}
            data-cy="utilityCard"
            borderColor={
              'light' as ResponsiveValue<ColorKey, Theme> & ('dark' | 'light')
            }
            variant="squared"
            mt="m"
          >
            <SuccessBox>
              <div style={{ width: '32px' }}>
                <CheckCircle size={32} weight="fill" color={Palette.green} />
              </div>
              <Text ml="s" variant="m" as="p">
                You’re good to go! You don’t need to set this up in your
                building.
              </Text>
            </SuccessBox>
          </GridCardColumn>
        )}
      </StyledCardsLayout>
    </>
  );
};

// TODO: we need to refactor and simplify this page once we have a single
// backend graphql query that returns the data that the frontend needs on
// the consumer side.  Here is an overview of the issue:
//
// Right now, we must query 5 different endpoints to render this page correctly:
//   1. /v2/item_definitions (to get the utility item, which contains the site entity id)
//   2. getUserInfo query (to get the user's address to display in the subheading)
//   3. getDivision query (to get the configuration of which utilities are turned on and off)
//   4. getUtilities query (to get a list of all utilities available at this property)
//   5. availableUtilityProviders (a fallback for users who aren't resident onboarded)
// We also need to load this data sequentially, because the graphql query requires a divisionId
// (site_entity_id in the payload).  The order of operations is as follows:
// REQUIRED FOR ALL USERS:
//   1. load the move
//   2. load the items (afaik, the graphql object for Move does not return the property's site_entity_id)
// Once we have the move and the items, we can scan the items to see if this user is in a property or not.
// If they are, we render the resident onboarding utilities component, which load the following:
//   a. getDivision: this is need to understand which of gas, electric and water is turned on or off
//   b. getUtilities: we have to get *all* utilities that have ever been configured at this address, then screen
//   out the ones for the utilities that are `false` from (a)
// If they aren't, we can render default utilities that are pegged to the user's address, not their
// property's preferred provider configuration.  That component will load another graphql query:
//   c. availableUtilityProviders: get utility at the *address*

interface UtilityData {
  loading: boolean;
  providers: {
    electricProviders: AvailableUtilityProviders[];
    waterProviders: AvailableUtilityProviders[];
    gasProviders: AvailableUtilityProviders[];
  };
}

export const useUtilities = (divisionId: string): UtilityData => {
  const divisionUtilities = useQuery<getUtilities, getUtilitiesVariables>(
    GetUtilitiesQuery,
    {
      variables: {
        input: {
          divisionId,
        },
      },
    }
  );

  const division = useQuery<getDivision, getDivisionVariables>(GetDivision, {
    variables: {
      divisionId,
    },
  });

  const loading = divisionUtilities.loading || division.loading;
  const divisionUtilitiesHasData =
    divisionUtilities.data?.availableUtilityProviders?.utilityProviders?.length;

  const providers: UtilityData['providers'] = useMemo(() => {
    if (!loading && divisionUtilitiesHasData) {
      const { utilityProviders } =
        divisionUtilities?.data?.availableUtilityProviders || {};
      const { utilitiesConfig } = division?.data?.division || {};
      const availableUtilityProviders: AvailableUtilityProviders[] = [];
      utilityProviders?.map((provider) => {
        availableUtilityProviders.push({
          ...provider,
          logoImage: provider?.logoImage
            ? {
                ...provider.logoImage,
                url: null,
                baseUrl: null,
              }
            : null,
        } as AvailableUtilityProviders);
      });

      // Here, we need to iterate through all of the available preferred providers at this property;
      // the backend will return *all* providers in the GetUtilities query.  so we check the division
      // settings to see which ones need to be displayed

      // if there is no utilities config from the division, return generic utilites from availableUtilities query
      if (utilitiesConfig === undefined) {
        const filteredProviders = pickUtilities(
          utilityProviders as AvailableUtilityProviders[]
        );
        return {
          gasProviders: filteredProviders.filter((p) => p.kind === 'gas'),
          waterProviders: filteredProviders.filter((p) => p.kind === 'water'),
          electricProviders: filteredProviders.filter(
            (p) => p.kind === 'electric'
          ),
        };
      }
      return {
        electricProviders:
          utilitiesConfig?.electric && utilityProviders?.length
            ? availableUtilityProviders?.filter(
                enabledOfKind('electric' as UtilityKinds) as () => boolean
              )
            : [],
        waterProviders:
          utilitiesConfig?.water && utilityProviders?.length
            ? availableUtilityProviders?.filter(
                enabledOfKind('water' as UtilityKinds) as () => boolean
              )
            : [],
        gasProviders:
          utilitiesConfig?.gas && utilityProviders?.length
            ? availableUtilityProviders?.filter(
                enabledOfKind('gas' as UtilityKinds) as () => boolean
              )
            : [],
      };
    }

    return {
      electricProviders: [],
      waterProviders: [],
      gasProviders: [],
    };
  }, [loading, division, divisionUtilities, divisionUtilitiesHasData]);

  return { loading, providers };
};

export const ResidentOnboardingUtilities = ({
  divisionId,
}: {
  divisionId: string;
}) => {
  const { loading, providers } = useUtilities(divisionId);
  const { electricProviders, waterProviders, gasProviders } = providers;
  const hasNoUtilities =
    !loading &&
    !electricProviders?.length &&
    !waterProviders?.length &&
    !gasProviders?.length;

  if (!loading && !hasNoUtilities) {
    const allProviders = [
      {
        type: 'Electric',
        providers: electricProviders,
      },
      {
        type: 'Gas',
        providers: gasProviders,
      },
      {
        type: 'Water',
        providers: waterProviders,
      },
    ];

    return (
      <>
        {allProviders.map((providerGroup) => {
          return (
            <UtilityProviderGroup
              data-testid="utility-provider-group"
              providerGroup={providerGroup}
              key={providerGroup.type}
            />
          );
        })}
      </>
    );
  }
  if (hasNoUtilities) {
    return <UtilityProviderGroup data-testid="utility-no-provider-group" />;
  }

  return null;
};

export const GenericUtilities = ({
  moveInfo,
  divisionId,
}: {
  moveInfo: QueryResult<getUserInfo, getUserInfoVariables>;
  divisionId: string;
}) => {
  const router = useRouter();
  const utilities = useQuery<
    availableUtilityProviders,
    availableUtilityProvidersVariables
  >(GetUsersUtilityProvidersQuery, {
    variables: {
      input: {
        divisionId: divisionId,
        address: {
          id: moveInfo.data?.getUser?.user?.currentMove?.toAddress?.id,
          street: moveInfo.data?.getUser?.user?.currentMove?.toAddress?.street,
          unit: moveInfo.data?.getUser?.user?.currentMove?.toAddress?.unit,
          state: moveInfo.data?.getUser?.user?.currentMove?.toAddress?.state,
          city: moveInfo.data?.getUser?.user?.currentMove?.toAddress?.city,
          postalCode:
            moveInfo.data?.getUser?.user?.currentMove?.toAddress?.postalCode,
        },
      },
    },
    skip:
      moveInfo.loading || !moveInfo.data?.getUser?.user?.currentMove?.toAddress,
  });

  const { loading } = utilities;

  const { gasProviders, waterProviders, electricProviders } = useMemo(() => {
    if (!loading && utilities?.data?.availableUtilityProviders) {
      const {
        availableUtilityProviders: { utilityProviders },
      } = utilities.data;
      const filteredProviders = pickUtilities(
        utilityProviders as AvailableUtilityProviders[]
      );
      return {
        gasProviders: filteredProviders.filter((p) => p.kind === 'gas'),
        waterProviders: filteredProviders.filter((p) => p.kind === 'water'),
        electricProviders: filteredProviders.filter(
          (p) => p.kind === 'electric'
        ),
      };
    }
    return {
      gasProviders: [],
      waterProviders: [],
      electricProviders: [],
    };
  }, [loading, utilities]);

  useEffect(() => {
    const providerLength =
      gasProviders.length + waterProviders.length + electricProviders.length;
    if (!loading && providerLength === 0) {
      router.push(ERROR_PAGE_PATH);
    }
  }, [loading, gasProviders, waterProviders, electricProviders, router]);

  if (!loading && utilities) {
    const allProviders = [
      {
        type: 'Electric',
        providers: electricProviders,
      },
      {
        type: 'Gas',
        providers: gasProviders,
      },
      {
        type: 'Water',
        providers: waterProviders,
      },
    ];

    return (
      <>
        {allProviders.map((providerGroup) => {
          return (
            <UtilityProviderGroup
              providerGroup={providerGroup}
              key={providerGroup.type}
            />
          );
        })}
      </>
    );
  }
  return null;
};

/* Hook for making rest call to mark the utilities as done */
export const useUpdateUtilities = () => {
  const { axios } = useRESTClient();

  const updateUtility = useCallback(
    async (currentMoveId: string | undefined) => {
      if (currentMoveId) {
        const result = await axios.post(
          `/v2/moves/${currentMoveId}/utility/already_done`
        );
        return result;
      }

      return null;
    },
    [axios]
  );

  return {
    updateUtility,
  };
};

// This is the final exported component, which loads one of two inner components
// based on whether the backend returns a valid site_entity_id (aka divisionId aka
// property ID)
export const Utilities: NextPage = () => {
  const { Track, trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: DOMAIN.UTILITIES,
  });

  const shouldRouteToDaisyChain = useShouldRouteToUPODaisyChain();
  const { updateUtility } = useUpdateUtilities();

  // function to get back to essentials - update task as done
  const router = useRouter();

  useEffect(() => {
    trackEvent({
      verb: VERB.VIEWED,
      pageName: PAGE.UTILITIES,
      object: OBJECT.UTILITIES,
    });
  }, [trackEvent]);

  // get information about the move from the standard move query:
  const moveInfo = useGetUserInfoQuery();
  const resOnboardingEnabled =
    moveInfo?.data?.getUser?.user.currentMove?.residentOnboardingEnabled;

  // This is a superfluous call, but the move query in graphql doesn't report
  // the property id associated with the move.  the only place this information
  // is guaranteed to be available is on the utility item, which is set for every
  // user:
  const { data: itemsData, error: itemsError } = useSWRWhenAuthed(
    '/v2/item_definitions'
  );

  /* Handle the submit of the form when it is required, but there is no
  proof required */

  async function handleCompleteUtilities(currentMoveId: string | undefined) {
    try {
      const res = await updateUtility(currentMoveId);
      if (res) {
        if (shouldRouteToDaisyChain) {
          router.push({
            pathname: '/well-done/[step]',
            query: { step: 'resident_onboarding-proof-of-utilities' },
          });
        } else {
          router.push('/'); // send back to essentials
        }
      }
    } catch (e) {
      const message = 'Something went wrong!';
    }
  }

  const { divisionId, loading, error } = useMemo(() => {
    if (itemsData && moveInfo.loading === false) {
      const utilityItem = itemsData.find(
        (item: { code: string }) => item.code === 'utility'
      );

      // new logic: we only want to display the utilities according to building;
      // the `entity` object can be multiple types: companies, real estate agents,
      // or buildings.  you need to check the type of entity explicitly in order to
      // understand whether this is a building or not.  Also, the key is called `object`.

      return {
        loading: false,
        divisionId: utilityItem?.entity?.id ?? undefined,
      };
    }
    if (itemsError || moveInfo.error) {
      return {
        loading: false,
        error: 'Error loading utility information for your address.',
      };
    }
    return {
      loading: true,
    };
  }, [itemsError, itemsData, moveInfo.error, moveInfo.loading]);

  if (!loading && moveInfo && itemsData) {
    const { getUser } = moveInfo.data || {};
    const { id: currentMoveId } = getUser?.user?.currentMove || {};
    const { toAddress } = getUser?.user?.currentMove || {};
    const toAddressLine = toAddress?.unit
      ? `${toAddress?.street}, #${toAddress?.unit}`
      : toAddress?.street;

    return (
      <Track>
        <PageTemplate
          title={COPY.heading}
          subtitle={`${toAddressLine}, ${toAddress?.city}, ${toAddress?.state}`}
          maxWidth={null}
          loading={loading}
          error={error}
        >
          <ResidentOnboardingUtilities divisionId={divisionId} />
          <Box maxWidth={465}>
            <Button
              mt="xl"
              size="l"
              onClick={() => handleCompleteUtilities(currentMoveId)}
              dataCy="utilities_no_action_done"
              data-testid="utilities_no_action_done"
              variant="primary"
              isFluidWidth
            >
              All done!
            </Button>
          </Box>
        </PageTemplate>
      </Track>
    );
  }
  return null;
};
